import { useEffect, useState } from 'react';
import { getTopClientes } from '@/lib/api';

// ── tipos (espelham admin-api/routers/top_clientes.py) ───────────────────────

interface Origem {
  canal: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  gclid: boolean;
  referral: boolean;
}

interface Campanha {
  utm_source: string | null;
  utm_campaign: string | null;
  eventos: number;
  ultimo_toque: string | null;
}

interface ProximaAcao {
  tipo: 'REATIVAR' | 'UPSELL' | 'RETER';
  acao: string;
}

interface Cliente {
  rank: number;
  org_id: number;
  name: string;
  plan_type: string;
  email: string | null;
  org_created_at: string | null;
  receita_brl: number;
  n_compras: number;
  creditos_comprados: number;
  saldo: number;
  primeira_compra: string | null;
  ultima_compra: string | null;
  ultimo_consumo: string | null;
  n_calculos: number;
  ultimo_login: string | null;
  origem: Origem;
  campanhas: Campanha[];
  reativado_recente: boolean;
  reativado_em: string | null;
  reativado_por: 'compra' | 'uso' | null;
  proxima_acao: ProximaAcao;
}

interface TopClientesData {
  disponivel: boolean;
  erro?: string;
  as_of: string;
  clientes: Cliente[];
  params: { reativacao_dias: number; dormencia_min_dias: number };
}

const fmtData = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('pt-BR') : '—';

const fmtBRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const diasAtras = (s: string | null): number | null =>
  s ? Math.floor((Date.now() - new Date(s).getTime()) / 86_400_000) : null;

const ACAO_BADGE: Record<ProximaAcao['tipo'], string> = {
  REATIVAR: 'bg-rose-50 text-rose-600',
  UPSELL: 'bg-violet-50 text-violet-600',
  RETER: 'bg-emerald-50 text-emerald-600',
};

const ORIGEM_BADGE: Record<string, string> = {
  'Google Ads': 'bg-blue-50 text-blue-600',
  'Meta Ads': 'bg-indigo-50 text-indigo-600',
  'Indicação (share)': 'bg-teal-50 text-teal-600',
  'Orgânico/direto': 'bg-slate-100 text-slate-500',
};

function CompraRecencia({ data }: { data: string | null }) {
  const dias = diasAtras(data);
  const cor =
    dias === null
      ? 'text-slate-400'
      : dias <= 30
        ? 'text-emerald-600'
        : dias <= 90
          ? 'text-amber-600'
          : 'text-rose-500';
  return (
    <span className={cor}>
      {fmtData(data)}
      {dias !== null && (
        <span className="text-xs text-slate-400"> ({dias}d)</span>
      )}
    </span>
  );
}

export default function TopClientesPage() {
  const [data, setData] = useState<TopClientesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [aberta, setAberta] = useState<number | null>(null);

  useEffect(() => {
    getTopClientes()
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading && !data)
    return <div className="p-6 text-slate-400">Carregando…</div>;
  if (!data) return <div className="p-6 text-slate-400">Sem dados.</div>;
  if (!data.disponivel)
    return <div className="p-6 text-rose-500">Indisponível: {data.erro}</div>;

  const receitaTotal = data.clientes.reduce((s, c) => s + c.receita_brl, 0);

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-800">
          30 Clientes Top
        </h1>
        <p className="text-sm text-slate-500">
          Maiores clientes da história por receita real (SÓ compras Stripe).
          Juntos: <strong>{fmtBRL(receitaTotal)}</strong>. &quot;Reativado&quot;
          = comprou ou calculou nos últimos {data.params.reativacao_dias}d após
          ≥{data.params.dormencia_min_dias}d parado. Ação sugerida é
          heurística — contato externo assina a Ingrid.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Cliente</th>
              <th className="px-3 py-2 text-left">Origem</th>
              <th className="px-3 py-2 text-left">Campanhas</th>
              <th className="px-3 py-2 text-right">Receita</th>
              <th className="px-3 py-2 text-right">Compras</th>
              <th className="px-3 py-2 text-left">Última compra</th>
              <th className="px-3 py-2 text-right">Saldo</th>
              <th className="px-3 py-2 text-left">Último cálculo</th>
              <th className="px-3 py-2 text-left">Reativado</th>
              <th className="px-3 py-2 text-left">Próxima ação</th>
            </tr>
          </thead>
          <tbody>
            {data.clientes.map((c) => (
              <tr
                key={c.org_id}
                onClick={() =>
                  setAberta(aberta === c.org_id ? null : c.org_id)
                }
                className="border-t border-slate-100 cursor-pointer align-top hover:bg-slate-50"
                title="Clique para expandir campanhas e detalhes"
              >
                <td className="px-3 py-2 text-slate-400">{c.rank}</td>
                <td className="px-3 py-2 text-slate-700">
                  <div className="font-medium">
                    {c.name}
                    <span className="text-xs text-slate-400"> #{c.org_id}</span>
                  </div>
                  <div className="text-xs text-slate-400">{c.email ?? '—'}</div>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs ${
                      ORIGEM_BADGE[c.origem.canal] ??
                      'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {c.origem.canal}
                  </span>
                  {c.origem.utm_campaign && (
                    <div className="mt-0.5 max-w-[140px] truncate text-xs text-slate-400">
                      {c.origem.utm_campaign}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {c.campanhas.length === 0 && '—'}
                  {(aberta === c.org_id
                    ? c.campanhas
                    : c.campanhas.slice(0, 2)
                  ).map((cp, i) => (
                    <div key={i} className="max-w-[160px] truncate">
                      {cp.utm_campaign ?? cp.utm_source ?? '?'}
                      <span className="text-slate-300"> ·{cp.eventos}ev</span>
                    </div>
                  ))}
                  {aberta !== c.org_id && c.campanhas.length > 2 && (
                    <div className="text-slate-300">
                      +{c.campanhas.length - 2} — clique
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-semibold text-slate-800">
                  {fmtBRL(c.receita_brl)}
                </td>
                <td className="px-3 py-2 text-right text-slate-600">
                  {c.n_compras}
                  <div className="text-xs text-slate-400">
                    {c.creditos_comprados}cr
                  </div>
                </td>
                <td className="px-3 py-2">
                  <CompraRecencia data={c.ultima_compra} />
                  {aberta === c.org_id && (
                    <div className="text-xs text-slate-400">
                      1ª: {fmtData(c.primeira_compra)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-slate-600">
                  {c.saldo}
                </td>
                <td className="px-3 py-2">
                  <CompraRecencia data={c.ultimo_consumo} />
                  {aberta === c.org_id && (
                    <div className="text-xs text-slate-400">
                      {c.n_calculos} cálculos · login{' '}
                      {fmtData(c.ultimo_login)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {c.reativado_recente ? (
                    <span className="inline-block rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-600">
                      ✓ por {c.reativado_por} ({fmtData(c.reativado_em)})
                    </span>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${ACAO_BADGE[c.proxima_acao.tipo]}`}
                  >
                    {c.proxima_acao.tipo}
                  </span>
                  <div className="mt-0.5 max-w-[260px] text-xs text-slate-500">
                    {c.proxima_acao.acao}
                  </div>
                </td>
              </tr>
            ))}
            {data.clientes.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-4 text-center text-slate-400">
                  Nenhuma compra Stripe registrada ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
